// Fliboe frontend bootstrap — build cache buster: 2026-02-23-cors-fix
import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "@/lib/auth";
import { PlayerProvider } from "@/lib/player";
import { initPWA } from "@/lib/pwa";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import PersistentPlayer from "@/components/PersistentPlayer";
import SupportChat from "@/components/SupportChat";
import TipPrompt from "@/components/TipPrompt";
import InstallPrompt from "@/components/InstallPrompt";
import Landing from "@/pages/Landing";
import StationDetail from "@/pages/StationDetail";
import Dashboard from "@/pages/Dashboard";
import AuthCallback from "@/pages/AuthCallback";
import Pricing from "@/pages/Pricing";
import EmbedPlayer from "@/pages/EmbedPlayer";
import PromoCard from "@/pages/PromoCard";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import Admin from "@/pages/Admin";
import Ampliboe from "@/pages/Ampliboe";
import BillingSuccess from "@/pages/BillingSuccess";
import Brochure from "@/pages/Brochure";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Advertise from "@/pages/Advertise";
import AdUpload from "@/pages/AdUpload";
import Produce from "@/pages/Produce";
import ProduceWizard from "@/pages/ProduceWizard";
import FreeSample from "@/pages/FreeSample";
import Blog from "@/pages/Blog";
import Pulse from "@/pages/Pulse";
import BrochureA5 from "@/pages/BrochureA5";
import BrochureSunoFliboe from "@/pages/BrochureSunoFliboe";
import BrochureFlip from "@/pages/BrochureFlip";
import BookReader from "@/pages/BookReader";
import ChaptersCatalog from "@/pages/ChaptersCatalog";
import Publish from "@/pages/Publish";
import AuthorPage from "@/pages/AuthorPage";
import { CasinoLanding, CasinoLogin, CasinoPlay, CasinoAuthCallback } from "@/pages/Casino";
import { AstroLanding, AstroLogin, AstroApp, AstroAuthCallback } from "@/pages/Astro";
import { ContractorsLanding, ContractorsLogin, ContractorsDashboard, ContractorsAuthCallback } from "@/pages/Contractors";
import { ShowoLanding, ShowoStudio, ShowoPlayer, ShowoLogin, ShowoAuthCallback } from "@/pages/Showo";
import Argus from "@/pages/Argus";
import ArgusTip from "@/pages/ArgusTip";
import ArgusWhip from "@/pages/ArgusWhip";
import ArgusLegal from "@/pages/ArgusLegal";
import ArgusLawyers from "@/pages/ArgusLawyers";
import ArgusContact from "@/pages/ArgusContact";
import ShowoMake from "@/pages/ShowoMake";
import { ShowoMakeAbout, ShowoMakeContact, ShowoMakeAdmin } from "@/pages/ShowoMakeExtras";
import { MonsoonLanding, MonsoonPlay, MonsoonLogin, MonsoonAuthCallback } from "@/pages/Monsoon";
import { FlipLanding, FlipWrite, FlipLogin, FlipAuthCallback } from "@/pages/Flip";
import AdminOnlyBrochure from "@/pages/AdminOnlyBrochure";
import PulseSiteNew from "@/pages/PulseSiteNew";
import PulseSiteDetail from "@/pages/PulseSiteDetail";
import PulsePost from "@/pages/PulsePost";
import PulseAbout from "@/pages/PulseAbout";
import Redeem from "@/pages/Redeem";

function AppRouter() {
  const location = useLocation();

  // Scroll to top on every route change so users land at the top of the new
  // page, not wherever the previous page was scrolled.
  useEffect(() => {
    // Skip auto-scroll if URL has a #hash anchor (let the browser/router handle it)
    if (!location.hash) {
      window.scrollTo({ top: 0, left: 0, behavior: "instant" in window ? "instant" : "auto" });
    }
  }, [location.pathname, location.hash]);

  // Google Auth callback dispatch. Each sub-app has its OWN callback route
  // that stores the session token under its OWN localStorage key. Without
  // this pathname check the fallback swallowed astro/contractors/casino
  // callbacks and dumped users into the FM control room.
  if (location.hash?.includes("session_id=")) {
    if (location.pathname.startsWith("/casino")) return <CasinoAuthCallback />;
    if (location.pathname.startsWith("/astro")) return <AstroAuthCallback />;
    if (location.pathname.startsWith("/contractors") || location.pathname.startsWith("/fixo")) return <ContractorsAuthCallback />;
    return <AuthCallback />;
  }

  // Chromeless full-bleed routes (print/screenshot ready brochures, etc).
  // Renders the page WITHOUT Header / Footer / PersistentPlayer so the entire
  // viewport is the brochure canvas — clean export.
  // Admin-gated: only admin role can view; everyone else is redirected.
  if (location.pathname.startsWith("/brochure/")) {
    return (
      <Routes>
        <Route path="/brochure/artist" element={<AdminOnlyBrochure variant="artist" />} />
        <Route path="/brochure/business" element={<AdminOnlyBrochure variant="business" />} />
        <Route path="/brochure/suno-fliboe" element={<BrochureSunoFliboe />} />
      </Routes>
    );
  }
  if (location.pathname.startsWith("/embed/")) {
    return (
      <Routes>
        <Route path="/embed/:id" element={<EmbedPlayer />} />
      </Routes>
    );
  }
  // Fliboe Play — social casino. Isolated route tree with no shared FM header,
  // no PersistentPlayer, no FM/Flip nav bleed-through. Own branded header lives
  // inside each Casino* component.
  if (location.pathname.startsWith("/casino")) {
    return (
      <Routes>
        <Route path="/casino" element={<CasinoLanding />} />
        <Route path="/casino/login" element={<CasinoLogin />} />
        <Route path="/casino/play" element={<CasinoPlay />} />
        <Route path="/casino/auth-callback" element={<CasinoAuthCallback />} />
      </Routes>
    );
  }
  // Fliboe Astro — audio astrology PWA. Isolated route tree, own token key.
  if (location.pathname.startsWith("/astro")) {
    return (
      <Routes>
        <Route path="/astro" element={<AstroLanding />} />
        <Route path="/astro/login" element={<AstroLogin />} />
        <Route path="/astro/reading" element={<AstroApp />} />
        <Route path="/astro/auth-callback" element={<AstroAuthCallback />} />
      </Routes>
    );
  }
  // Fixo (formerly Fliboe Contractors) — home-improvement PWA. Isolated
  // route tree. `/contractors/*` and `/fixo/*` both work; `/fixo` is the new
  // brand-forward URL. Keep both to avoid breaking any deployed links.
  if (location.pathname.startsWith("/contractors") || location.pathname.startsWith("/fixo")) {
    return (
      <Routes>
        <Route path="/contractors" element={<ContractorsLanding />} />
        <Route path="/contractors/login" element={<ContractorsLogin />} />
        <Route path="/contractors/dashboard" element={<ContractorsDashboard />} />
        <Route path="/contractors/auth-callback" element={<ContractorsAuthCallback />} />
        <Route path="/fixo" element={<ContractorsLanding />} />
        <Route path="/fixo/login" element={<ContractorsLogin />} />
        <Route path="/fixo/dashboard" element={<ContractorsDashboard />} />
        <Route path="/fixo/auth-callback" element={<ContractorsAuthCallback />} />
      </Routes>
    );
  }
  // Argus — private investigative research desk (isolated tree).
  if (location.pathname.startsWith("/argus")) {
    return (
      <Routes>
        <Route path="/argus" element={<Argus />} />
        <Route path="/argus/whip" element={<ArgusWhip />} />
        <Route path="/argus/lawyers" element={<ArgusLawyers />} />
        <Route path="/argus/contact" element={<ArgusContact />} />
        <Route path="/argus/terms" element={<ArgusLegal />} />
        <Route path="/argus/privacy" element={<ArgusLegal />} />
        <Route path="/argus/tip/:slug" element={<ArgusTip />} />
      </Routes>
    );
  }
  // Fliboe Showo — video platform (shorts / films / series / music videos).
  // Uses the main fliboe_token so Flip authors publish without re-signup.
  if (location.pathname.startsWith("/showo")) {
    return (
      <Routes>
        <Route path="/showo" element={<ShowoLanding />} />
        <Route path="/showo/make" element={<ShowoMake />} />
        <Route path="/showo/make/about" element={<ShowoMakeAbout />} />
        <Route path="/showo/make/contact" element={<ShowoMakeContact />} />
        <Route path="/showo/make/admin" element={<ShowoMakeAdmin />} />
        <Route path="/showo/login" element={<ShowoLogin />} />
        <Route path="/showo/auth-callback" element={<ShowoAuthCallback />} />
        <Route path="/showo/studio" element={<ShowoStudio />} />
        <Route path="/showo/f/:id" element={<ShowoPlayer />} />
      </Routes>
    );
  }
  if (location.pathname.startsWith("/monsoon")) {
    return (
      <Routes>
        <Route path="/monsoon" element={<MonsoonLanding />} />
        <Route path="/monsoon/login" element={<MonsoonLogin />} />
        <Route path="/monsoon/auth-callback" element={<MonsoonAuthCallback />} />
        <Route path="/monsoon/play" element={<MonsoonPlay />} />
      </Routes>
    );
  }
  if (location.pathname.startsWith("/flip")) {
    return (
      <Routes>
        <Route path="/flip" element={<FlipLanding />} />
        <Route path="/flip/login" element={<FlipLogin />} />
        <Route path="/flip/auth-callback" element={<FlipAuthCallback />} />
        <Route path="/flip/write" element={<FlipWrite />} />
      </Routes>
    );
  }
  return (
    <>
      <Header />
      <div className="min-h-[60vh]">
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/s/:id" element={<StationDetail />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/promo" element={<PromoCard />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/ampliboe" element={<Ampliboe />} />
          <Route path="/billing/success" element={<BillingSuccess />} />
          <Route path="/brochure" element={<Brochure />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/advertise" element={<Advertise />} />
          <Route path="/ads/upload/:token" element={<AdUpload />} />
          <Route path="/produce" element={<Produce />} />
          <Route path="/produce/wizard/:token" element={<ProduceWizard />} />
          <Route path="/free-sample" element={<FreeSample />} />
          <Route path="/free-sample/:id" element={<FreeSample />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/pulse" element={<Pulse />} />
          <Route path="/pulse/new" element={<PulseSiteNew />} />
          <Route path="/pulse/:id" element={<PulseSiteDetail />} />
          <Route path="/p/:slug" element={<PulsePost />} />
          <Route path="/pulse/about" element={<PulseAbout />} />
          <Route path="/redeem/:code" element={<Redeem />} />
          <Route path="/book/:slug" element={<BookReader />} />
          <Route path="/flip" element={<ChaptersCatalog />} />
          <Route path="/chapters" element={<ChaptersCatalog />} />
          <Route path="/publish" element={<Publish />} />
          <Route path="/author/:slug" element={<AuthorPage />} />
          <Route path="/brochure/artist" element={<BrochureA5 variant="artist" />} />
          <Route path="/brochure/business" element={<BrochureA5 variant="business" />} />
          <Route path="/brochure/suno-fliboe" element={<BrochureSunoFliboe />} />
          <Route path="/brochure/flip" element={<BrochureFlip />} />
          <Route path="/flyer/flip" element={<BrochureFlip />} />
          <Route path="*" element={<Landing />} />
        </Routes>
      </div>
      <Footer />
      <PersistentPlayer />
      <TipPrompt />
      <SupportChat />
      <InstallPrompt />
    </>
  );
}

function App() {
  initPWA();
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <PlayerProvider>
            <AppRouter />
          </PlayerProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
